import { useState, useEffect } from 'react';
import AppHome from './AppHome';
import InvoiceGenerator from './InvoiceGenerator';
import Setup from './Setup';
import CustomerForm from './CustomerForm';
import InstallExtension from './InstallExtension';
import AlertModal from './components/AlertModal';
import { useAlert } from './hooks/useAlert';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [isInIframe, setIsInIframe] = useState(false);
  const [isValidCustomfields, setIsValidCustomfields] = useState(false);
  const [showSetupNav, setShowSetupNav] = useState(false);
  const [showInvoiceNav, setShowInvoiceNav] = useState(false);
  const [loading, setLoading] = useState(true);
  const { alert: alertState, showAlert, closeAlert } = useAlert();

  useEffect(() => {
    const checkEnvironmentAndCustomFields = async () => {
      try {
        console.log('🚀 Starting environment and custom fields check...');

        // Check if running in iframe FIRST before anything else
        const inIframe = window.self !== window.top;
        setIsInIframe(inIframe);

        // Only check extension if we're actually in an iframe
        // (postMessageWithResponse hangs forever when there's no parent to respond)
        if (!inIframe) {
          console.log('🏠 Not in iframe - showing home page');
          setShowInvoiceNav(false);
          setShowSetupNav(false);
          setCurrentPage('home');
          return;
        }

        const isExtension = await fnCheckExtension();
        console.log('Extension Result:' + isExtension);
        //console.log('📱 Running in iframe:', inIframe);

        // // Check if custom fields are ready
        // const { isCusomfieldsReady,ApiValidator } = await import('./serverjs/customFields');
        // const customFieldsValid = await isCusomfieldsReady();
        // setIsValidCustomfields(customFieldsValid);
        // //console.log('✅ Custom fields valid:', customFieldsValid);

        // new implementation to check and create custom fields if not exist
        
        const { IsAllCustomfieldsValid } = await import('./serverjs/customFieldsNew');
        const validationResult = await IsAllCustomfieldsValid();
        const customFieldsValid = validationResult.status === 'success';
        setIsValidCustomfields(customFieldsValid);

        // Determine navigation visibility based on conditions
        if (inIframe) {
          //console.log('📍 In iframe - determining navigation visibility...');

          // If the extension handler is 'Extension', show the install page
          if (isExtension === 'Extensions') {
            console.log('🔌 Extension detected - showing install extension page');
            setShowSetupNav(false);
            setShowInvoiceNav(false);
            setCurrentPage('install');
          } else if (customFieldsValid) {
            console.log('✨ Custom fields valid - showing invoice nav');
            setShowInvoiceNav(true);
            setShowSetupNav(true);
            // If custom fields are valid, show invoice page by default
            setCurrentPage('invoice');
          } else {
            console.log('⚠️ Custom fields NOT valid - hiding invoice nav, showing setup');

            // if custom fields not generated then we must generate here.
            //const validationResult = await ApiValidator();
            
            if (customFieldsValid) {
              showAlert('success', 'Success', 'Custom fields created successfully!');
              setIsValidCustomfields(true);
              setShowInvoiceNav(true);
              setCurrentPage('invoice');
            } else {
              showAlert('error', 'Error', 'Failed to create custom fields. Please try again.');
              setShowInvoiceNav(false);
              setCurrentPage('setup');
            }
            setShowSetupNav(true);
          }
        }

        // console.log('📊 Final state:', {
        //   showInvoiceNav: inIframe && customFieldsValid,
        //   showSetupNav: inIframe,
        //   currentPage: inIframe ? (customFieldsValid ? 'invoice' : 'setup') : 'home'
        // });
      } catch (error) {
        console.error('❌ Error checking environment:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
        // Default to safe state
        setShowInvoiceNav(false);
        setShowSetupNav(false);
        setCurrentPage('home');
      } finally {
        setLoading(false);
      }
    };

    checkEnvironmentAndCustomFields();
  }, []);


  async function postMessageWithResponse(message) {
    return new Promise((resolve) => {
      const requestId = crypto.randomUUID();

      function onMessage(event) {
        if (event.data?.type?.endsWith('-response') && event.data?.requestId === requestId) {
          window.removeEventListener('message', onMessage);
          resolve(event.data.body);
        }
      }

      window.addEventListener('message', onMessage);
      window.parent.postMessage({ ...message, requestId }, '*');
    });
  }


  async function fnCheckExtension() {
    const { managerApi } = await import('./serverjs/jofotaraFuncs.js');

     const formResponse = await postMessageWithResponse({
          type: 'page-request'
        });
     console.log(formResponse);
     return formResponse?.handler;
  }

  const navigateTo = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <AlertModal
        isOpen={alertState.isOpen}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={closeAlert}
      />

      {/* Navigation Bar */}
      {(showSetupNav || showInvoiceNav) && (
        <nav className="bg-gray-800/50 backdrop-blur-md border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <h1 className="text-white text-xl font-bold">FIRS GO!</h1>
                </div>
                <div className="ml-10 flex items-baseline space-x-4">
                  {showInvoiceNav && (
                    <button
                      onClick={() => navigateTo('invoice')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 'invoice'
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                    >
                      Invoice Generator
                    </button>
                  )}
                  {showInvoiceNav && (
                    <button
                      onClick={() => navigateTo('customer')}
                      className={`hidden px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 'customer'
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                    >
                      Customer Details
                    </button>
                  )}
                  {showSetupNav && (
                    <button
                      onClick={() => navigateTo('setup')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 'setup'
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                    >
                      Setup
                    </button>
                  )}
                  {showSetupNav && (
                    <button
                      onClick={() => navigateTo('install')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 'install'
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                    >
                      Install Extension
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Page Content */}
      <div>
        {currentPage === 'home' && <AppHome />}
        {currentPage === 'invoice' && <InvoiceGenerator />}
        {currentPage === 'customer' && <CustomerForm customerId={null} onCancel={() => navigateTo('invoice')} />}
        {currentPage === 'setup' && <Setup />}
        {currentPage === 'install' && <InstallExtension />}
      </div>
    </div>
  );
}

export default App;
